import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Link2, RefreshCw, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";

const Wishlist = () => {
  const { user, wishlistIds } = useAuth();
  const [items, setItems] = useState([]);
  const [shareUrl, setShareUrl] = useState("");
  const [shareActive, setShareActive] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/me/wishlist").then((response) => setItems(response.data));
    api.get("/me/wishlist/share")
      .then((response) => setShareActive(response.data.active))
      .catch(() => setShareActive(false));
  }, [user]);

  const createShare = async () => {
    setSharing(true);
    try {
      const response = await api.post("/me/wishlist/share");
      setShareUrl(response.data.share_url);
      setShareActive(true);
      try {
        await navigator.clipboard?.writeText(response.data.share_url);
      } catch {
        // The link remains visible when a browser blocks automatic clipboard access.
      }
      toast.success("Wishlist link is ready to share");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create a wishlist link");
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Wishlist link copied");
    } catch {
      toast.error("Copy the link from the field below");
    }
  };

  const revokeShare = async () => {
    try {
      await api.delete("/me/wishlist/share");
      setShareUrl("");
      setShareActive(false);
      toast.success("Wishlist link revoked");
    } catch {
      toast.error("Could not revoke the wishlist link");
    }
  };

  if (!user) {
    return (
      <div className="py-20 text-center">
        Please <Link to="/login" className="text-[#C86D42]">login</Link> to see your wishlist.
      </div>
    );
  }

  const wishlistItems = items.filter((product) => wishlistIds.includes(product.id));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:px-12">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8C6D46]">
            Saved for later
          </p>
          <h1 className="mt-2 font-serif text-4xl font-semibold">Your Wishlist</h1>
        </div>
        <button
          type="button"
          onClick={createShare}
          disabled={sharing || !wishlistItems.length}
          className={[
            "inline-flex items-center gap-2 rounded-lg bg-[#2D5A47] px-4 py-3 text-sm",
            "font-semibold text-white transition-colors hover:bg-[#214435]",
            "disabled:cursor-not-allowed disabled:bg-[#CBD5CF]",
          ].join(" ")}
          data-testid="share-wishlist-button"
        >
          {shareActive ? <RefreshCw size={16} /> : <Share2 size={16} />}
          {sharing ? "Creating link…" : shareActive ? "Refresh share link" : "Share Wishlist"}
        </button>
      </div>
      {shareUrl && (
        <section
          className="mb-8 border border-[#D9C3A5] bg-[#FDFBF7] p-4 sm:p-5"
          data-testid="wishlist-share-panel"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="font-serif text-2xl font-semibold text-[#1E2A24]">
                A link for gifting
              </h2>
              <p className="mt-1 text-sm text-[#64748B]">
                Anyone with this link can browse this wishlist.
              </p>
            </div>
            <button
              type="button"
              onClick={revokeShare}
              className={[
                "inline-flex items-center gap-1 text-sm font-semibold text-[#A63A2B]",
                "hover:underline",
              ].join(" ")}
              data-testid="revoke-wishlist-share-button"
            >
              <X size={16} /> Revoke link
            </button>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <div
              className={[
                "flex min-w-0 flex-1 items-center gap-2 border border-[#E2DCD2] bg-white",
                "px-3 py-2 text-sm text-[#64748B]",
              ].join(" ")}
              data-testid="wishlist-share-url"
            >
              <Link2 size={16} className="shrink-0 text-[#2D5A47]" />
              <span className="truncate">{shareUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyShare}
              className={[
                "inline-flex items-center justify-center gap-2 border border-[#2D5A47] px-4",
                "py-2 text-sm font-semibold text-[#2D5A47] hover:bg-[#EBF2EE]",
              ].join(" ")}
              data-testid="copy-wishlist-share-button"
            >
              <Copy size={16} /> Copy link
            </button>
          </div>
        </section>
      )}
      {wishlistItems.length === 0 ? (
        <div className="py-20 text-center text-[#64748B]">Your wishlist is empty.</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
          {wishlistItems.map((product) => <ProductCard key={product.id} product={product} />)}
        </div>
      )}
    </div>
  );
};

export default Wishlist;